import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2 } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import BrandMark from "@/components/common/BrandMark";

export default function AcceptInvite() {
  const { token } = useParams();
  const { t, locale, setLocale } = useI18n();
  const navigate = useNavigate();
  const [info, setInfo] = useState(undefined);
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/invitations/${token}`, { _skipAuthRedirect: true })
      .then((r) => setInfo(r.data))
      .catch(() => setInfo(null));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) { setError(t("validation.min_password")); return; }
    setSaving(true);
    try {
      await api.post("/invitations/accept", { token, full_name: fullName, password }, { _skipAuthRedirect: true });
      setDone(true);
    } catch (err) { setError(apiErrorMessage(err, t)); } finally { setSaving(false); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6 dark:bg-slate-950">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BrandMark className="h-8 w-8" />
            <span className="font-heading font-bold">Mayr-Technologies</span>
          </div>
          <button onClick={() => setLocale(locale === "de" ? "en" : "de")} className="text-xs font-medium uppercase text-slate-500">{locale === "de" ? "EN" : "DE"}</button>
        </div>

        {info === undefined ? (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : info === null || !info.valid ? (
          <p className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-950 dark:text-red-300" data-testid="invite-invalid">{t("auth.invite_invalid")}</p>
        ) : done ? (
          <div className="rounded-md bg-emerald-50 p-6 text-center dark:bg-emerald-950" data-testid="invite-done">
            <CheckCircle2 className="mx-auto h-8 w-8 text-emerald-600" />
            <p className="mt-2 text-sm text-emerald-800 dark:text-emerald-300">{t("auth.invite_accepted")}</p>
            <Button className="mt-4 w-full" onClick={() => navigate("/login")} data-testid="invite-goto-login">{t("auth.login_button")}</Button>
          </div>
        ) : (
          <>
            <h1 className="font-heading text-2xl font-bold">{t("auth.invite_title")}</h1>
            <p className="mt-1 text-sm text-slate-500">{t("auth.invite_subtitle", { tenant: info.tenant_name })}</p>
            <p className="mt-2 text-sm text-slate-500">{info.email} · {t(`roles.${info.tenant_role}`)}</p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div className="space-y-1.5"><Label>{t("auth.full_name")}</Label><Input required value={fullName} onChange={(e) => setFullName(e.target.value)} data-testid="invite-name-input" /></div>
              <div className="space-y-1.5"><Label>{t("auth.password")}</Label><Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="invite-password-input" /></div>
              {error && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950 dark:text-red-300" data-testid="invite-error">{error}</p>}
              <Button type="submit" className="w-full" disabled={saving} data-testid="invite-submit">{saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}{t("auth.accept_invite")}</Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
